import React, { useState } from 'react';
import { Form, Input, message, Card, Select, Spin, Alert } from 'antd';
import { useNavigate } from 'react-router-dom';
import { insertData } from '../../../services/api';
import { useGetProjSystems } from '../../../hooks/genericHooks';
import type { ProjSystems, ToPlnProjSubsystems } from '../../../types';
import Button from '../../../components/common/Button';

const ToPlnProjSubSystemsCreate: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);

  // Fetch all available systems for select
  const { data: systemsData, isLoading: systemsLoading, error: systemsError } = useGetProjSystems();
  const systemsList: ProjSystems[] = systemsData?.data || [];

  const handleFinish = async (values: Partial<ToPlnProjSubsystems>) => {
    setIsCreating(true);
    try {
      await insertData('to_pln_proj_subsystems', {
        subsystem_id: values.subsystem_id,
        subsystem_name: values.subsystem_name,
        system_id: values.system_id,
        status: values.status,
        imc_group: values.imc_group,
        priority: values.priority,
        requirement: values.requirement,
        remarks: values.remarks,
      });
      message.success('Subsystem created successfully!');
      navigate(-1);
    } catch (err: any) {
      message.error('Creation failed: ' + (err?.message || 'Unknown error'));
    } finally {
      setIsCreating(false);
    }
  };

  if (systemsLoading) return <Spin />;
  if (systemsError) return <Alert type="error" message="Error loading systems" description={systemsError?.message || 'Unknown error'} />;

  return (
    <Card variant="outlined" title="Create Project Subsystem" style={{ maxWidth: 500, margin: '2rem auto' }}>
      <Form form={form} layout="vertical" onFinish={handleFinish} initialValues={{ status: 'active' }} style={{ gap: 0, rowGap: 0 }}>
        <Form.Item label="Associated System" name="system_id" rules={[{ required: true, message: 'Please select a system' }]} style={{ marginBottom: 8 }}>
          <Select placeholder="Select a system">
            {systemsList.map(sys => (
              <Select.Option key={sys.sys_id} value={sys.sys_id}>{sys.sys_id} - {sys.sys_name}</Select.Option>
            ))}
          </Select>
        </Form.Item>
        <div style={{ display: 'flex', gap: 8, marginBottom: 0 }}>
          <Form.Item label="Subsystem ID" name="subsystem_id" rules={[{ required: true, message: 'Please enter subsystem ID' }]} style={{ flex: 1, marginBottom: 0 }}>
            <Input />
          </Form.Item>
          <Form.Item label="Status" name="status" rules={[{ required: true, message: 'Please select a status' }]} style={{ flex: 1, marginBottom: 0 }}>
            <Select>
              <Select.Option value="active">Active</Select.Option>
              <Select.Option value="inactive">Inactive</Select.Option>
              <Select.Option value="out_of_service">Out of Service</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item label="IMC Group" name="imc_group" style={{ flex: 1, marginBottom: 0 }}>
            <Input type="number" />
          </Form.Item>
        </div>
        <Form.Item label="Subsystem Name" name="subsystem_name" rules={[{ required: true, message: 'Please enter subsystem name' }]} style={{ marginBottom: 8 }}>
          <Input />
        </Form.Item>
        <Form.Item label="Priority" name="priority" style={{ marginBottom: 8 }}>
          <Input type="number" />
        </Form.Item>
        <Form.Item label="Requirement" name="requirement" style={{ marginBottom: 8 }}>
          <Input />
        </Form.Item>
        <Form.Item label="Remarks" name="remarks" style={{ marginBottom: 8 }}>
          <Input />
        </Form.Item>
        <Form.Item style={{ marginBottom: 8 }}>
          <Button action="add" type="primary" htmlType="submit" loading={isCreating}>
            Create
          </Button>
          <Button style={{ marginLeft: 8 }} onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default ToPlnProjSubSystemsCreate;
